import { delay } from '@util/async';
import { clearCache, checkStatus } from '@util/cache';

/**
 * HOST - constant value used to build url in buildURl function below
 */
const HOST = '';

/**
 * RETRY_SCALE_FACTOR - A semi-arbitrary value to tweak scaling the exponential
 * backoff when retrying fetches.
 */
const RETRY_SCALE_FACTOR = 1500;

/**
 * MAX_TRIES - How many times retryFetch will attempt to retry a failed fetch.
 */
const MAX_TRIES = 5;

/**
 * MAX_DELAY - How many milliseconds is the maximum delay time between attemps.
 */
const MAX_DELAY = 20000;

/**
 * getRetryDelay - Full jitter exponential backoff based on
 * https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/
 *
 * @param {number} attempt The number of the current attempt to fetch data.
 *
 *@returns {number} A time in milliseconds to delay the next attempt.
 */
const getRetryDelay = attempt => {
  // Calculate a delay time based on how many attempts have been made.
  const delayTime = Math.min(MAX_DELAY, RETRY_SCALE_FACTOR * 2 ** attempt);
  // Return a random value between zero and the calculated delay time.
  return Math.round(Math.random() * delayTime);
};

/**
 * retryFetch - If a fetch fails as a result of a connections error,
 * retry the fetch after an amount of time determined by the RETRY_SCALE_FACTOR,
 * and how many fetches have been attempted so far.
 *
 * @param {string} url The url to be passed to native fetch.
 * @param {object} options Options to be passed to native fetch call.
 * @param {number} count How many times to retry the fetch.
 *
 * @return {Promise} The successful fetch call, or another attempted fetch.
 */
export const retryFetch = (url, options, count = 1) =>
  fetch(url, options).catch(ex => {
    if (count > MAX_TRIES) {
      // eslint-disable-next-line no-param-reassign
      ex.data = {
        exceptionText: 'There was a problem with your network connection. Please try again.'
      };
      throw ex;
    }

    if (
      ex.message === 'Failed to fetch' ||
      ex.message === 'NetworkError when attempting to fetch resource.'
    ) {
      /**
       * delayTime - After each attempt, the delay between attemps grows larger;
       * alleviating pressure on an overloaded server.
       */
      const delayTime = getRetryDelay(count);
      return delay(delayTime).then(() => retryFetch(url, options, count + 1));
    }

    throw ex;
  });

/**
 * buildUrl - For our caching layer to work we use urls at keys; this function builds the url used for
 * the key. The keys need to be formatted consistently to make sure they match everywhere. Hence
 * this function.
 *
 * @param {string} url Url that is used as key
 *
 * @return {string} URL after it's been processed.
 */
export const buildUrl = url => {
  // Ensure url preceding slash:
  const firstSlash = url.slice(0, 1) === '/';
  return `${HOST}${firstSlash ? url : `/${url}`}`;
};

/**
 * resultTypeHandler - Some of our APIs return a Promise that needs to be converted to JSON so that
 * we can consume them. Other times APIS return data that has already been converted. This function
 * abstracts that check.
 *
 * @param {(object|Promise)} result Item returned from API
 *
 * @return {object} Either json that results form result.json or response from API that is already
 * JSON
 */
const resultTypeHandler = async result => {
  const contentType = result.headers.get('content-type');
  const object =
    contentType && contentType.indexOf('application/json') !== -1
      ? await result.json()
      : { data: [] };

  return object.data || object;
};

/**
 * executeFetch - Common set of operations when performing a fetch and
 * processing the result.
 *
 * @param {string} url  URL of the API
 * @param {object} init Configuration options of the fetch API
 *
 * @returns {promise} A promise which resolves in the result of the API call
 */
export const executeFetch = async (url, init) => {
  const key = buildUrl(url);
  clearCache(key);
  const response = await fetch(key, init);
  const result = await checkStatus(response);
  return resultTypeHandler(result);
};
